import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';

import Button from '../../components/reusable/button/button.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import OrdersTable from '../../components/reusable/tables/orders-table/orders-table.component';
import Pagination from '../../components/reusable/pagination/pagination.component';
import PullSheet from '../../components/order/pull-sheet/pull-sheet.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import TableControls from '../../components/reusable/table-controls/table-controls.component';

import useTableState from '../../hooks/useTableState';
import Client from '../../tools/client';
import { ensureSocketConnected, onSafe } from '../../tools/socket';

import {
  ContentContainer,
  MainContainer,
  MainTitle
} from '../../styles/page.styles';

const client = new Client();

const OrdersPage = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersCount, setOrdersCount] = useState(null);
  const [showPullSheet, setShowPullSheet] = useState(false);

  const {
    page,
    size,
    search,
    sortKey,
    sortDirection,
    filters,
    setPage,
    setSize,
    setSearch,
    setSortKey,
    setSortDirection,
    setFilters
  } = useTableState({ defaultSize: 25 });

  const isInitialMount = useRef(true);

  const buildQuery = useCallback(() => {
    let query = `?page=${page}&size=${size}`;
    if (search) query += `&search=${encodeURIComponent(search)}`;
    if (sortDirection) query += `&sortDirection=${sortDirection}`;
    if (sortKey) query += `&sortKey=${sortKey}`;

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query += `&filter[${key}]=${value.join(',')}`;
      } else if (value) {
        if (key === 'dateStart') {
          query += `&filter[startDate]=${value}`;
        } else if (key === 'dateEnd') {
          query += `&filter[endDate]=${value}`;
        } else {
          query += `&filter[${key}]=${encodeURIComponent(value)}`;
        }
      }
    });

    return query;
  }, [page, size, search, sortKey, sortDirection, filters]);

  const getOrders = useCallback(async () => {
    setLoading(true);
    const query = buildQuery();
    const res = await client.getOrders(query);
    setOrders(res.rows);
    setOrdersCount(res.count);
    setLoading(false);
  }, [buildQuery]);

  useEffect(() => {
    getOrders();
    ensureSocketConnected();
    const offNewOrder = onSafe('newOrder', getOrders);
    return () => offNewOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    getOrders();
  }, [page, size, search, sortKey, sortDirection, filters, getOrders]);

  // No-op: setSearch in useTableState already resets page to 0.
  // Calling setPage(0) here would trigger a second setSearchParams call
  // that overwrites the search param due to React Router batching.
  const handleSearchSubmit = useCallback(() => {}, []);

  // Export configuration
  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return '';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const exportFn = async (currentFilters) => {
    let query = '?';
    if (sortDirection) query += `sortDirection=${sortDirection}&`;
    if (sortKey) query += `sortKey=${sortKey}&`;

    Object.entries(currentFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query += `filter[${key}]=${value.join(',')}&`;
      } else if (value) {
        if (key === 'dateStart') {
          query += `filter[startDate]=${value}&`;
        } else if (key === 'dateEnd') {
          query += `filter[endDate]=${value}&`;
        } else {
          query += `filter[${key}]=${value}&`;
        }
      }
    });

    const res = await client.exportOrders(query);
    return res.rows || [];
  };

  const formatExportData = (data) => {
    return data.map((order) => {
      const customerName = order.User ? `${order.User.firstName || ''} ${order.User.lastName || ''}`.trim() : '';
      const email = order.User?.email || '';
      const orderDate = dayjs(order.createdAt).format('MM/DD/YYYY');

      return [
        order.refId || '',
        order.status || '',
        customerName,
        email,
        orderDate,
        order.paymentType || '',
        order.paid ? 'Yes' : 'No',
        formatCurrency(order.subtotal),
        formatCurrency(order.discountTotal),
        formatCurrency(order.shippingTotal),
        formatCurrency(order.deliveryInsuranceTotal),
        formatCurrency(order.total)
      ];
    });
  };

  const exportHeaders = ['Ref ID', 'Status', 'Customer Name', 'Email', 'Order Date', 'Payment Type', 'Paid', 'Subtotal', 'Discount', 'Shipping', 'Insurance', 'Total'];

  const filterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'checkbox',
      options: ['new', 'billed', 'paid', 'processing', 'partially_shipped', 'shipped', 'canceled']
    },
    {
      key: 'date',
      label: 'Date Range',
      type: 'dateRange'
    }
  ];

  return (
    <MainContainer>
      <ComponentModal isOpen={showPullSheet} onClose={() => setShowPullSheet(false)}>
        <PullSheet />
      </ComponentModal>

      <ContentContainer>
        <MainTitle>Orders</MainTitle>

        <TableControls
          search={search}
          setSearch={setSearch}
          onSearchSubmit={handleSearchSubmit}
          size={size}
          setSize={setSize}
          filters={filters}
          setFilters={setFilters}
          filterConfig={filterConfig}
          entityName="orders"
          exportFn={exportFn}
          formatExportData={formatExportData}
          exportHeaders={exportHeaders}
          additionalButtons={
            <Button onClick={() => setShowPullSheet(true)}>Pull Sheet</Button>
          }
        />

        {loading ? (
          <Spinner />
        ) : (
          <>
            <OrdersTable
              orders={orders}
              sortKey={sortKey}
              setSortKey={setSortKey}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />

            <Pagination
              count={ordersCount}
              size={size}
              page={page}
              changePage={setPage}
            />
          </>
        )}
      </ContentContainer>
    </MainContainer>
  );
};

export default OrdersPage;
